/**
=========================================================
* Otis Admin PRO - v2.0.1
=========================================================

* Product Page: https://material-ui.com/store/items/otis-admin-pro-material-dashboard-react/
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from 'react';

// react-router-dom components
import { useLocation, NavLink } from 'react-router-dom';

// prop-types is a library for typechecking of props.
import PropTypes from 'prop-types';

// @mui material components
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Icon from '@mui/material/Icon';

// Otis Admin PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Otis Admin PRO React example components
import SidenavCollapse from 'examples/Sidenav/SidenavCollapse';
import SidenavList from 'examples/Sidenav/SidenavList';
import SidenavItem from 'examples/Sidenav/SidenavItem';

// Custom styles for the Sidenav
import SidenavRoot from 'examples/Sidenav/SidenavRoot';
import sidenavLogoLabel from 'examples/Sidenav/styles/sidenav';
import * as Styled from './styles/index.styles';

// Otis Admin PRO React context
import { useMaterialUIController, setMiniSidenav, setTransparentSidenav, setWhiteSidenav } from 'context';
import { HamburgerButton } from 'components/HamburgerButton/HamburgerButton';
import { Stack } from '@mui/system';
import { OEM } from 'utility/OEMProvider';
import navigationPaths from 'engine/navigationPaths';

import { useNavigate } from 'react-router-dom';
import { GeneralErrorMessageModal } from "components/GeneralErrorMessageModal/GeneralErrorMessageModal";
import { getUserPurchasePlanLimitStates } from "api/endpoints/getUserPurchasePlanLimitStates";

import React from "react";
import { Typography } from '@mui/material';

function Sidenav({ color, brand, brandMini, brandName, routes, ...rest }) {
  const [openCollapse, setOpenCollapse] = useState(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(false);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split('/').slice(1)[0];
  const items = pathname.split('/').slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];

  let textColor = 'white';
  
  const isBillzOEM = process.env.REACT_APP_OEM_NAME === 'billz';
  const navigate = useNavigate();
  const [errorDialogOpen, setErrorDialogOpen] = React.useState(false);

  const getData = async () => {
    return getUserPurchasePlanLimitStates();
  };

  const checkPurchasePlanLimitsExceeded = async (purchasePlanLimits) => {
    let limitExceeded = false;
    if(purchasePlanLimits && purchasePlanLimits.length > 0) {
      const data = await getData();
      purchasePlanLimits.map((key) => {
        if(data[key] === true) {
          limitExceeded = true;
        }
      });
    }

    return limitExceeded;
  }

  const handleErrorDialogConfirm = () => {
    setErrorDialogOpen(false);
    navigate(navigationPaths().Pricing);
  };

  const handleErrorDialogClose = () => {
      setErrorDialogOpen(false);
  };



  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = 'dark';
  } else if (whiteSidenav && darkMode) {
    textColor = 'inherit';
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    setOpenCollapse(collapseName);
    setOpenNestedCollapse(itemParentName);
  }, []);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /**
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener('resize', handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse) => {
    const template = collapse.map(({ name, route, key, href, cssBeforeName, purchasePlanLimits }) =>
      href ? (
        <Link key={key} href={href} target="_blank" rel="noreferrer" sx={{ textDecoration: 'none' }}>
          <SidenavItem name={name} cssBeforeName={cssBeforeName} nested />
        </Link>
      ) : (
        <NavLink to={route} key={key} sx={{ textDecoration: 'none' }}>
          <SidenavItem name={name} cssBeforeName={cssBeforeName} active={route === pathname} nested purchasePlanLimits={purchasePlanLimits} />
        </NavLink>
      ),
    );

    return template;
  };
  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses) =>
    collapses.map(({ name, collapse, route, href, key, state, type, cssBeforeName, purchasePlanLimits }) => {
      let returnValue;

      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            color={color}
            name={name}
            cssBeforeName={cssBeforeName}
            active={key === itemParentName ? 'isParent' : false}
            open={openNestedCollapse === key}
            onClick={({ currentTarget }) =>
              openNestedCollapse === key && currentTarget.classList.contains('MuiListItem-root')
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else if (type !== 'hidden') {
        returnValue = href ? (
          <Link href={href} key={key} target="_blank" rel="noreferrer" sx={{ textDecoration: 'none' }}>
            <SidenavItem color={color} name={name} cssBeforeName={cssBeforeName} active={key === itemName} />
          </Link>
        ) : (
          <NavLink to={route} key={key} state={state} sx={{ textDecoration: 'none' }} onClick={ async (e) => {
            if( purchasePlanLimits && purchasePlanLimits.length > 0) {
              e.preventDefault();
              e.stopPropagation();
              if(!(await checkPurchasePlanLimitsExceeded(purchasePlanLimits))) {
                navigate(route, {state: state});
              } else {
                setErrorDialogOpen(true);
              }
            }
  
          }}>
            <SidenavItem color={color} name={name} cssBeforeName={cssBeforeName} active={key === itemName}/>
          </NavLink>
        );
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, collapse, noCollapse, key, href, newWindow, cssBeforeName, route, state }) => {
    let returnValue;

    if (type === 'collapse') {
      returnValue = href ? (
        newWindow ? (
          <Link href={href} key={key} target={'_blank'} rel="noreferrer" sx={{ textDecoration: 'none' }}>
            <SidenavCollapse name={name} icon={icon} active={key === collapseName} noCollapse={noCollapse} />
          </Link>
        ) : (
          <NavLink to={href} key={key} sx={{ textDecoration: 'none' }}>
            <SidenavCollapse name={name} icon={icon} active={key === collapseName} noCollapse={noCollapse} />
          </NavLink>
        )
      ) : (
        <SidenavCollapse
          key={key}
          name={name}
          icon={icon}
          active={key === collapseName}
          open={openCollapse === key}
          onClick={() => (openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key))}
        >
          {collapse ? renderCollapse(collapse) : null}
        </SidenavCollapse>
      );
    } else if (type === 'title') {
      returnValue = !miniSidenav ? (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      ) : (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) || (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    } else if (type === 'divider') {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) || (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    } else if (type === 'main') {
      returnValue = (<MDBox px={2}>
        {href ? (
          <Link href={href} key={key} target="_blank" rel="noreferrer" sx={{ textDecoration: 'none' }}>
            <SidenavItem color={color} name={name} cssBeforeName={cssBeforeName} active={key === itemName} />
          </Link>
        ) : (
          <NavLink to={route} key={key} state={state} sx={{ textDecoration: 'none' }}>
            <SidenavItem color={color} icon={icon} name={name} cssBeforeName={cssBeforeName} active={key === itemName}/>
          </NavLink>
        )}
      </MDBox>)
    }

    return returnValue;
  });

 
  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}>
      {errorDialogOpen && <GeneralErrorMessageModal
        id="error-message"
        onClose={handleErrorDialogClose}
        onConfirm={handleErrorDialogConfirm}
        title="Limit wyczerpany" closeText="Przejdź do Planów cenowych"
        message="Przekroczony został dostępny limit tworzonych dokumentów."
      />} 
      <MDBox pt={3} pb={1} pl={4} pr={() => (!miniSidenav ? 1 : 4)} textAlign="center">
        <MDBox
          display={{ xs: 'block', xl: 'none' }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: 'pointer' }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: 'bold' }}>close</Icon>
          </MDTypography>
        </MDBox>
        <Stack direction="row" justifyContent={() => (!miniSidenav ? 'space-between' : 'center')} alignItems="center">
          {!miniSidenav && (
            <MDBox component={NavLink} to={navigationPaths().Dashboard} display="flex" alignItems="center">
              {!miniSidenav && (
                <Stack  direction="column" justifyContent="center" alignItems="flex-start">
                  <Styled.BrandBox component="img" src={brand} alt={OEM().ProductName}  />
                  {isBillzOEM && <MDTypography variant="h6" color="primary">BETA</MDTypography>}
                </Stack>
                )}
              {miniSidenav && <Styled.BrandBoxMini component="img" src={brandMini} alt={OEM().ProductName} />}
            </MDBox>
          )}
          <MDBox display={{ xs: 'none', xl: 'block' }}>
            <HamburgerButton />
          </MDBox>
        </Stack>
      </MDBox>
      <Divider
        light={(!darkMode && !whiteSidenav && !transparentSidenav) || (darkMode && !transparentSidenav && whiteSidenav)}
      />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: 'info',
  brand: '',
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(['primary', 'secondary', 'info', 'success', 'warning', 'error', 'dark']),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.instanceOf(Array).isRequired,
};

export default Sidenav;
